/**
 * Publish-to-Marketplace wizard (modal).
 *
 * Four steps:
 *   1. Anonymize  → POST /marketplace/anonymize?atto_id=…
 *   2. Review     → editable textarea of the redacted body + side-by-side
 *   3. Meta       → title / description / tags / category / price tier
 *   4. Publish    → POST /marketplace/templates then POST /publish (or save draft)
 */
import React, { useCallback, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { toast } from "sonner";
import {
  Sparkles, Loader2, ShieldCheck, ArrowRight, ArrowLeft, Tag, Wand2,
} from "lucide-react";

const PRICE_TIERS = [19, 29, 49, 99];

export function PublishToMarketplaceModal({ open, onOpenChange, atto }) {
  const { t } = useI18n();
  const [step, setStep] = useState(1); // 1..4
  const [anonymizing, setAnonymizing] = useState(false);
  const [anonymized, setAnonymized] = useState("");
  const [originalDocument, setOriginalDocument] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [category, setCategory] = useState("");
  const [price, setPrice] = useState(29);
  const [submitting, setSubmitting] = useState(false);

  // Reset everything whenever the modal is re-opened for a different atto.
  // Uses useImperativeLoad to align with the rest of the codebase and avoid
  // the react-hooks/set-state-in-effect lint warning.
  useImperativeLoad(() => {
    if (open && atto) {
      setStep(1);
      setAnonymized("");
      setOriginalDocument(atto.document || "");
      setTitle((atto.subject || "").slice(0, 120));
      setDescription("");
      setTags("");
      setCategory("");
      setPrice(29);
    }
  }, [open, atto?.atto_id]);

  const runAnonymize = useCallback(async () => {
    if (!atto?.atto_id) return;
    setAnonymizing(true);
    try {
      const r = await api.post("/marketplace/anonymize", { atto_id: atto.atto_id }, { timeout: 60000 });
      setAnonymized(r.data.anonymized || "");
      toast.success(t("marketplace.toast_anonymized"));
      setStep(2);
    } catch (e) {
      toast.error(t("marketplace.err_anonymize"));
    } finally {
      setAnonymizing(false);
    }
  }, [atto, t]);

  const authorShare = useMemo(() => (price - Math.round(price * 0.15 * 100) / 100).toFixed(2), [price]);

  const buildPayload = useCallback(() => ({
    source_atto_id: atto?.atto_id,
    atto_type: atto?.atto_type,
    label: atto?.label,
    title: title.trim(),
    description: description.trim(),
    tags: tags.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 10),
    category: category.trim() || null,
    anonymized_document: anonymized,
    price_eur: price,
  }), [atto, title, description, tags, category, anonymized, price]);

  const validateMeta = () => {
    if (title.trim().length < 8) return t("marketplace.publish_title_label") + " ≥ 8";
    if (description.trim().length < 20) return t("marketplace.publish_desc_label") + " ≥ 20";
    if (!PRICE_TIERS.includes(price)) return t("marketplace.err_invalid_price");
    if (anonymized.trim().length < 200) return t("marketplace.publish_anonymized_label") + " ≥ 200";
    return null;
  };

  const saveDraft = useCallback(async () => {
    const err = validateMeta();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await api.post("/marketplace/templates", buildPayload());
      toast.success(t("marketplace.toast_template_created"));
      onOpenChange(false);
    } catch (e) {
      toast.error(t("marketplace.err_create"));
    } finally {
      setSubmitting(false);
    }
  }, [buildPayload, onOpenChange, t]);

  const publishNow = useCallback(async () => {
    const err = validateMeta();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/marketplace/templates", buildPayload());
      const tplId = r.data.template_id;
      await api.post(`/marketplace/templates/${tplId}/publish`);
      toast.success(t("marketplace.toast_template_published"));
      onOpenChange(false);
    } catch (e) {
      toast.error(t("marketplace.err_publish"));
    } finally {
      setSubmitting(false);
    }
  }, [buildPayload, onOpenChange, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl border-slate-700 bg-[#0A0C1F] text-white" data-testid="publish-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t("marketplace.publish_modal_title")}</DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
          {[1, 2, 3, 4].map((n) => (
            <span
              key={n}
              className={`flex-1 rounded-full py-1.5 text-center transition-colors ${
                n === step ? "bg-[#9ACD32] text-[#05073B]" : n < step ? "bg-slate-700 text-slate-200" : "bg-slate-800 text-slate-500"
              }`}
            >
              {n === 1 && t("marketplace.publish_step_anonymize")}
              {n === 2 && t("marketplace.publish_step_review")}
              {n === 3 && t("marketplace.publish_step_meta")}
              {n === 4 && t("marketplace.publish_step_publish")}
            </span>
          ))}
        </div>

        {/* ─── Step 1: Anonymize ─── */}
        {step === 1 && (
          <div className="space-y-4 py-2" data-testid="publish-step-1">
            <div className="flex items-start gap-3 rounded-xl border border-[#9ACD32]/30 bg-[#9ACD32]/5 p-4 text-sm">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#9ACD32]" />
              <p className="text-slate-300">{t("marketplace.publish_anonymize_intro")}</p>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
              {t("marketplace.publish_original_label")} · {atto?.label} · {atto?.char_count} chars
            </p>
            <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <pre className="whitespace-pre-wrap font-serif text-[12px] leading-[1.6] text-slate-300">
                {originalDocument.slice(0, 2000)}{originalDocument.length > 2000 ? "…" : ""}
              </pre>
            </div>
            <Button
              onClick={runAnonymize}
              disabled={anonymizing}
              className="w-full rounded-full bg-[#9ACD32] py-6 text-base font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="publish-anonymize-btn"
            >
              {anonymizing ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("marketplace.publish_anonymize_running")}</>
              ) : (
                <><Wand2 className="mr-2 h-4 w-4" />{t("marketplace.publish_anonymize_cta")}</>
              )}
            </Button>
          </div>
        )}

        {/* ─── Step 2: Review ─── */}
        {step === 2 && (
          <div className="space-y-3 py-2" data-testid="publish-step-2">
            <p className="text-xs text-slate-400">{t("marketplace.publish_anonymized_label")}</p>
            <Textarea
              value={anonymized}
              onChange={(e) => setAnonymized(e.target.value)}
              rows={14}
              className="border-slate-700 bg-slate-900 font-serif text-sm text-white"
              data-testid="publish-anonymized-textarea"
            />
            <p className="text-[10px] text-slate-500">{anonymized.length} chars</p>
            <div className="flex justify-between gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800">
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={anonymized.length < 200}
                className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="publish-next-meta"
              >
                Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* ─── Step 3: Meta ─── */}
        {step === 3 && (
          <div className="space-y-4 py-2" data-testid="publish-step-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase text-slate-400">
                {t("marketplace.publish_title_label")}
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("marketplace.publish_title_ph")}
                className="border-slate-700 bg-slate-900 text-white"
                data-testid="publish-title-input"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase text-slate-400">
                {t("marketplace.publish_desc_label")}
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("marketplace.publish_desc_ph")}
                rows={4}
                className="border-slate-700 bg-slate-900 text-white"
                data-testid="publish-desc-input"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase text-slate-400">
                  {t("marketplace.publish_tags_label")}
                </label>
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder={t("marketplace.publish_tags_ph")}
                  className="border-slate-700 bg-slate-900 text-white"
                  data-testid="publish-tags-input"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase text-slate-400">
                  {t("marketplace.publish_category_label")}
                </label>
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder={t("marketplace.publish_category_ph")}
                  className="border-slate-700 bg-slate-900 text-white"
                  data-testid="publish-category-input"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase text-slate-400">
                {t("marketplace.publish_price_label")}
              </label>
              <div className="flex flex-wrap gap-2" data-testid="publish-price-tiers">
                {PRICE_TIERS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPrice(p)}
                    className={`rounded-full border px-4 py-2 font-mono text-sm font-bold transition-colors ${
                      price === p ? "border-[#9ACD32] bg-[#9ACD32] text-[#05073B]" : "border-slate-700 text-slate-300 hover:border-slate-500"
                    }`}
                    data-testid={`publish-price-${p}`}
                  >
                    €{p}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                {t("marketplace.publish_price_hint").replace("{author_share}", authorShare)}
              </p>
            </div>
            <div className="flex justify-between gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800">
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back
              </Button>
              <Button
                onClick={() => setStep(4)}
                className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="publish-next-confirm"
              >
                Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* ─── Step 4: Confirm/Publish ─── */}
        {step === 4 && (
          <div className="space-y-4 py-2" data-testid="publish-step-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#F4C955]">{atto?.label}</p>
              <h3 className="mt-1 font-display text-base font-bold text-white">{title}</h3>
              <p className="mt-1.5 line-clamp-2 text-sm text-slate-400">{description}</p>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {tags.split(",").map((tag, i) => tag.trim() && (
                    <span key={i} className="inline-flex items-center gap-0.5 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">
                      <Tag className="h-2.5 w-2.5" /> {tag.trim()}
                    </span>
                  ))}
                </div>
                <span className="rounded-full bg-[#9ACD32] px-3 py-1 font-mono text-sm font-extrabold text-[#05073B]">
                  €{price}
                </span>
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                {t("marketplace.publish_price_hint").replace("{author_share}", authorShare)}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button variant="outline" onClick={() => setStep(3)} className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800">
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back
              </Button>
              <Button
                onClick={saveDraft}
                disabled={submitting}
                variant="outline"
                className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
                data-testid="publish-save-draft"
              >
                {t("marketplace.publish_save_draft")}
              </Button>
              <Button
                onClick={publishNow}
                disabled={submitting}
                className="ml-auto rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="publish-publish-now"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Sparkles className="mr-1.5 h-3.5 w-3.5" />{t("marketplace.publish_publish")}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
